import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { EmailAuthProvider, getAuth, reauthenticateWithCredential } from "firebase/auth";
import { useAuthState } from "react-firebase-hooks/auth";
import { AlertCircle, ArrowLeftRight, CheckCircle2, Inbox, Send } from "lucide-react";
import Sidebar from "./Sidebar";
import Header from "../components/Header";
import { app } from "../firebase";
import { useStreams } from "../hooks/useStreams";
import { trackClick } from "../utils/analytics";
import {
  acceptTransfer,
  cancelTransfer,
  daysLeft,
  declineTransfer,
  fetchTransfers,
  offerStream,
  TransferOffer,
  Transfers,
} from "../utils/streamTransfer";

const card = "bg-white rounded-3xl p-8 shadow-sm border border-slate-100";
const input = "w-full bg-white border border-slate-200 rounded-2xl py-3 px-4 text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500";

const expiry = (offer: TransferOffer) => {
  const days = daysLeft(offer.expiresAt);
  return days <= 1 ? "Expires today or tomorrow" : `Expires in ${days} days`;
};

const TransfersPage: React.FC = () => {
  const [user] = useAuthState(getAuth(app));
  const { streams, selected, reload, select } = useStreams();
  const [params] = useSearchParams();
  const [transfers, setTransfers] = useState<Transfers | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [stream, setStream] = useState(params.get("stream") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ type: "ok" | "error"; text: string } | null>(null);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      setTransfers(await fetchTransfers(() => user.getIdToken()));
      setLoadFailed(false);
    } catch (error) {
      console.error(error);
      setLoadFailed(true);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const chosen = stream || selected?.name || streams[0]?.name || "";
  const incoming = transfers?.incoming ?? [];
  const outgoing = transfers?.outgoing ?? [];

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.email || !chosen || !email.trim() || !password) return;
    setBusy(true);
    setNotice(null);
    try {
      await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password));
    } catch {
      setBusy(false);
      setNotice({ type: "error", text: "That password isn't right." });
      return;
    }
    trackClick("button_click-transfer_stream");
    const result = await offerStream(() => user.getIdToken(true), chosen, email.trim());
    setBusy(false);
    if (result.status === "sent") {
      setNotice({ type: "ok", text: `We emailed ${email.trim()}. ${chosen} moves when they accept; you keep it until then.` });
      setEmail("");
      setPassword("");
      await load();
    } else if (result.status === "wait") {
      setNotice({ type: "error", text: `Please wait ${result.retryAfterSeconds} seconds before trying again.` });
    } else if (result.status === "reauthenticate") {
      setNotice({ type: "error", text: "Please enter your password again." });
    } else {
      setNotice({ type: "error", text: result.message });
    }
  };

  const decide = async (offer: TransferOffer, action: "accept" | "decline" | "cancel") => {
    if (!user) return;
    setBusy(true);
    setNotice(null);
    const run = action === "accept" ? acceptTransfer : action === "decline" ? declineTransfer : cancelTransfer;
    const result = await run(() => user.getIdToken(true), offer.id);
    setBusy(false);
    if (!result.ok) {
      setNotice({ type: "error", text: result.message });
    } else if (action === "accept") {
      await reload();
      select(offer.streamName);
      setNotice({ type: "ok", text: `${offer.streamName} is now on your account, with its saved transcripts and glossary. If you were at your stream limit, it was raised to fit.` });
    } else {
      setNotice({ type: "ok", text: action === "decline" ? "Offer declined." : "Offer cancelled." });
    }
    await load();
  };

  return (
    <div className="flex flex-col h-screen bg-slate-50 font-inter overflow-hidden">
      <Header />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar activeItem="streams" />
        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-4xl mx-auto space-y-8">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 tracking-tight">Transfer a stream</h1>
              <p className="text-slate-500 mt-1 text-lg">
                Hand a stream, with its saved transcripts and glossary, to another account.{" "}
                <Link to="/streams" className="text-blue-600 hover:underline font-medium">Back to streams</Link>
              </p>
            </div>

            {notice && (
              <div role={notice.type === "error" ? "alert" : "status"} className={`p-4 rounded-2xl border flex items-start gap-3 text-sm font-medium ${notice.type === "ok" ? "bg-green-50 text-green-700 border-green-100" : "bg-red-50 text-red-700 border-red-100"}`}>
                {notice.type === "ok" ? <CheckCircle2 size={18} className="mt-0.5 shrink-0" /> : <AlertCircle size={18} className="mt-0.5 shrink-0" />}
                {notice.text}
              </div>
            )}
            {loadFailed && (
              <div role="alert" className="p-4 rounded-2xl bg-red-50 text-red-700 border border-red-100 text-sm font-medium flex items-center gap-3">
                <AlertCircle size={18} /> We couldn't load your transfers. Refresh to try again.
              </div>
            )}

            {incoming.length > 0 && (
              <section className={card} aria-labelledby="incoming-title">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-blue-50 rounded-lg text-blue-600"><Inbox size={20} /></div>
                  <h2 id="incoming-title" className="text-lg font-bold text-slate-900">Offered to you</h2>
                </div>
                <ul className="divide-y divide-slate-100">
                  {incoming.map((o) => (
                    <li key={o.id} className="py-4 flex flex-col md:flex-row md:items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900 break-all">{o.streamName}</p>
                        <p className="text-sm text-slate-500 break-all">From {o.fromEmail}. {expiry(o)}.</p>
                      </div>
                      <div className="flex gap-2">
                        <button disabled={busy} onClick={() => decide(o, "accept")} className="btn-primary disabled:opacity-60">Accept</button>
                        <button disabled={busy} onClick={() => decide(o, "decline")} className="btn-secondary disabled:opacity-60">Decline</button>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className={card} aria-labelledby="offer-title">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600"><ArrowLeftRight size={20} /></div>
                <h2 id="offer-title" className="text-lg font-bold text-slate-900">Offer one of your streams</h2>
              </div>
              <p className="text-slate-500 text-sm mb-6">
                The other person signs in with the email you enter and accepts. Until then the stream stays yours. Questions, networking contacts and the quiz go with it; your API keys and billing stay with you.
              </p>
              <form onSubmit={send} className="space-y-5">
                <div>
                  <label htmlFor="transfer-stream" className="block text-sm font-semibold text-slate-700 mb-2 ml-1">Stream</label>
                  <select id="transfer-stream" value={chosen} onChange={(e) => setStream(e.target.value)} className={input}>
                    {streams.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="transfer-email" className="block text-sm font-semibold text-slate-700 mb-2 ml-1">Their email address</label>
                  <input id="transfer-email" type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} className={input} />
                </div>
                <div>
                  <label htmlFor="transfer-password" className="block text-sm font-semibold text-slate-700 mb-2 ml-1">Your password</label>
                  <input id="transfer-password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className={input} />
                </div>
                <button type="submit" disabled={busy || !chosen || !email.trim() || !password} className="btn-primary flex items-center gap-2 disabled:opacity-60">
                  <Send size={18} /> {busy ? "Sending..." : "Send offer"}
                </button>
              </form>
            </section>

            {outgoing.length > 0 && (
              <section className={card} aria-labelledby="outgoing-title">
                <h2 id="outgoing-title" className="text-lg font-bold text-slate-900 mb-4">Waiting for an answer</h2>
                <ul className="divide-y divide-slate-100">
                  {outgoing.map((o) => (
                    <li key={o.id} className="py-4 flex flex-col md:flex-row md:items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900 break-all">{o.streamName}</p>
                        <p className="text-sm text-slate-500 break-all">To {o.toEmail}. {expiry(o)}.</p>
                      </div>
                      <button disabled={busy} onClick={() => decide(o, "cancel")} className="btn-secondary disabled:opacity-60">Cancel offer</button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default TransfersPage;
